import { useCallback } from 'react';
import { useParams } from 'next/navigation';

import { uniq } from 'lodash';

import { useFeatureFlagsValues } from '@/hooks/featureFlag/useFeatureFlag';

import { formatUrlPathname } from '@/utils/urls/formatUrl';

import { networkHasAccess, networkHasAccessWithFeatureFlag } from './pageHasAccess';

type CheckNetworkAccessParams = {
    network: string;
    page: string;
    params?: Record<string, any>;
    featureFlagsValues: Record<string, boolean>;
};

/**
 * Check if a social network has access to a specific page.
 * Handles both standard access rules and feature flag overrides.
 *
 * @param network - The social network type (e.g., 'instagram', 'facebook', 'x')
 * @param page - The target page URL to check access for
 * @param params - Optional URL params for dynamic routes (e.g., { id: '123' })
 * @param featureFlagsValues - Object containing feature flag values
 * @returns true if the network has access, false otherwise
 *
 * @example
 * const hasAccess = checkNetworkAccess({
 *   network: 'x',
 *   page: '/analyze/overview',
 *   params: {},
 *   featureFlagsValues: { x_analytics_active: true }
 * });
 */
export const checkNetworkAccess = ({
    network,
    page,
    params = {},
    featureFlagsValues
}: CheckNetworkAccessParams): boolean => {
    if (!network || !page) return true;

    const formattedUrl = formatUrlPathname(page, params);

    // If the page is not in networkHasAccess, all networks have access
    if (!(formattedUrl in networkHasAccess)) return true;

    // Check if the social network is in the list of authorized networks
    const hasAccess = networkHasAccess[formattedUrl].includes(network);

    // Check if there's a feature flag override for this page/network combo
    if (
        !hasAccess &&
        networkHasAccessWithFeatureFlag &&
        formattedUrl in networkHasAccessWithFeatureFlag
    ) {
        const pageOverrides = networkHasAccessWithFeatureFlag[formattedUrl];
        if (network in pageOverrides) {
            const featureFlagName = pageOverrides[network];
            return featureFlagsValues[featureFlagName] === true;
        }
    }

    return hasAccess;
};

/**
 * Hook to check if a social network has access to a specific page.
 * Automatically handles feature flags extraction and provides a simple check function.
 *
 * @returns An object with a checkAccess function
 *
 * @example
 * const { checkAccess } = useCheckNetworkAccess();
 * const hasAccess = checkAccess('instagram', '/analyze/overview');
 */
export const useCheckNetworkAccess = () => {
    const params = useParams();

    // Extract all unique feature flags from networkHasAccessWithFeatureFlag
    const featureFlags = uniq(
        Object.values(networkHasAccessWithFeatureFlag ?? {}).flatMap(Object.values)
    );
    const featureFlagsValues = useFeatureFlagsValues(featureFlags);

    /**
     * Check if a social network has access to a specific page
     * @param network - The social network type (e.g., 'instagram', 'facebook')
     * @param page - The target page URL to check access for
     * @returns true if the network has access, false otherwise
     */
    const checkAccess = useCallback(
        (network: string, page: string): boolean => {
            return checkNetworkAccess({
                network,
                page,
                params,
                featureFlagsValues
            });
        },
        [params, featureFlagsValues]
    );

    return { checkAccess };
};
